// /admin/General Settings — admin-editable configuration for the directory.
//
// The canonical reference data lives in static JSON under frontend/src/data/
// and backend/services/:
//   countries.json       — every ISO2 country (supported set)
//   countryNames.js      — ISO2 → display name
//   regions.json         — UN-5 region → ISO2 countries
//   subdivisions.json    — ISO 3166-2 per country
//   categories.json      — canonical category taxonomy + osm_native flag
//
// The only list this page persists is `enabled_countries` (the ISO2 subset
// that surfaces in the directory). Everything else is either static (render
// directly from the JSON), or configuration singletons (map tags, feature
// flags, auto-categorization trigger words).
//
// Previously this page had CRUD for Service Regions (legacy AU state list)
// and Based States — both removed 2026-04-17 after the UN-5 + ISO 3166-2
// migration. The old Add/Edit/Merge dialogs for region/state/category went
// with them.

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { authGet } from '../../utils/auth';
import { Plus, Pencil, Trash2, ArrowLeft, MapPin, Tag, Globe, Hash, GripVertical, Settings2, Key, BarChart2, RefreshCw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import COUNTRIES_DATA from '@/data/countries.json';
import COUNTRY_NAMES from '@/data/countryNames';
import CATEGORIES_DATA from '@/data/categories.json';

const API = '/api';

// Canonical country list derived from countries.json — sorted by display name.
const ALL_COUNTRIES = Object.keys(COUNTRIES_DATA)
  .map(code => ({ code, name: COUNTRY_NAMES[code] || code }))
  .sort((a, b) => a.name.localeCompare(b.name));

// Canonical category list derived from categories.json (skip the _doc entry).
// These names are locked against add/edit/delete in the admin — they map to
// OSM tags and BTCMap sync, so renaming or deleting them breaks the pipeline.
const ALL_CATEGORY_NAMES = CATEGORIES_DATA
  .filter(c => c.slug)
  .map(c => c.name)
  .sort((a, b) => a.localeCompare(b));

const TABS = [
  { id: 'countries',      label: 'Countries',      icon: Globe,      color: 'text-blue-600' },
  { id: 'categories',     label: 'Categories',     icon: Tag,        color: 'text-blue-600' },
  { id: 'tags',           label: 'Map Tags',       icon: Hash,       color: 'text-purple-600' },
  { id: 'features',       label: 'Feature Flags',  icon: Settings2,  color: 'text-amber-600' },
  { id: 'state_coverage', label: 'States Coverage',icon: BarChart2,  color: 'text-indigo-600' },
];

// ─── Sortable Map Tag row ────────────────────────────────────────────────────
const SortableTagItem = ({ tag, onEdit, onDelete, onToggleVisibility, counts }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tag.name });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const tagCounts = counts?.find(c => c.tag === tag.name) || { orangelabel_count: 0, btcmap_count: 0 };
  return (
    <tr ref={setNodeRef} style={style} className={`${isDragging ? 'bg-muted' : ''} ${tag.visible === false ? 'opacity-50' : ''}`}>
      <td className="px-4 py-2 w-10">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing" title="Drag to reorder">
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </button>
      </td>
      <td className="px-4 py-2 font-medium">{tag.name}</td>
      <td className="px-4 py-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
            {tagCounts.orangelabel_count || 0}
          </span>
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
            {tagCounts.btcmap_count || 0}
          </span>
        </div>
      </td>
      <td className="px-4 py-2 text-sm text-muted-foreground">
        {tag.categories?.length ? tag.categories.slice(0, 3).join(', ') + (tag.categories.length > 3 ? ` +${tag.categories.length - 3}` : '') : 'All'}
      </td>
      <td className="px-4 py-2 text-right">
        <div className="flex gap-2 justify-end items-center">
          <Switch checked={tag.visible !== false} onCheckedChange={() => onToggleVisibility(tag)} />
          <button onClick={() => onEdit(tag)} className="p-1.5 hover:bg-accent rounded transition-colors" title="Edit">
            <Pencil className="w-4 h-4 text-muted-foreground" />
          </button>
          {tag.name !== 'All' && (
            <button onClick={() => onDelete(tag.name)} className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors" title="Delete">
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

const GeneralSettings = () => {
  const [activeTab, setActiveTab] = useState('countries');
  // `null` means "no explicit filter" → every country is enabled.
  // A Set instance means the user has opted into a specific subset.
  const [enabledCountries, setEnabledCountries] = useState(null);
  // Country toggles are batched locally and persisted via an explicit Save —
  // each POST busts an expensive server-side cache, so we avoid per-toggle saves.
  const [countriesDirty, setCountriesDirty] = useState(false);
  const [mapTags, setMapTags] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [tagCounts, setTagCounts] = useState([]);
  const [triggerWords, setTriggerWords] = useState([]);
  const [osmCategories, setOsmCategories] = useState([]);
  const [expandedCats, setExpandedCats] = useState({});
  const [osmFilter, setOsmFilter] = useState('');
  const [loading, setLoading] = useState(true);

  // Feature flags
  // `google_maps_api_key` holds ONLY a newly-typed key — never the stored
  // secret (the backend returns it masked + a `key_set` flag). A blank save
  // preserves the existing key.
  const [featureFlags, setFeatureFlags] = useState({
    google_maps_autocomplete: false,
    google_maps_api_key: '',
    google_maps_key_set: false,
  });
  const [savingFeatures, setSavingFeatures] = useState(false);

  // State coverage
  const [stateCoverage, setStateCoverage] = useState([]);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [backfillStatus, setBackfillStatus] = useState(null);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [expandedCountry, setExpandedCountry] = useState(null);
  // Holds the active backfill-status poll so it can be cleared on unmount and
  // never duplicated.
  const backfillPollRef = useRef(null);

  // Country search
  const [countrySearch, setCountrySearch] = useState('');

  // Trigger-words edit dialog (categories are read-only; only the trigger
  // words + businesses-in-category view open via this dialog).
  const [triggerDialog, setTriggerDialog] = useState(false);
  const [triggerEdit, setTriggerEdit] = useState({ category: '', triggers: '', businesses: [] });

  // Map Tag dialog
  const [mapTagDialog, setMapTagDialog] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [tagForm, setTagForm] = useState({ name: '', categories: [], visible: true });

  const navigate = useNavigate();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // ─── Data fetching ─────────────────────────────────────────────────────────
  const fetchSettings = async () => {
    try {
      const r = await axios.get(`${API}/admin/general-settings`);
      const raw = r.data?.enabled_countries;
      // Empty array or missing → "all enabled". Non-empty → explicit subset.
      if (Array.isArray(raw) && raw.length > 0) {
        setEnabledCountries(new Set(raw.map(c => String(c).toUpperCase())));
      } else {
        setEnabledCountries(null);
      }
    } catch (e) {
      console.error('Error fetching settings:', e);
      setEnabledCountries(null);
    }
    setLoading(false);
  };

  const fetchMapTags = async () => {
    try {
      const r = await axios.get(`${API}/admin/map-tags`);
      setMapTags(Array.isArray(r.data) ? r.data : (r.data?.tags || []));
    } catch (e) { console.error('Error fetching map tags:', e); }
  };

  const fetchCategoryCounts = async () => {
    try {
      const r = await axios.get(`${API}/admin/category-counts`);
      setCategoryCounts(r.data?.counts || {});
    } catch (e) { console.error('Error fetching category counts:', e); }
  };

  const fetchTagCounts = async () => {
    try {
      const r = await axios.get(`${API}/admin/map-tag-counts`);
      setTagCounts(Array.isArray(r.data) ? r.data : (r.data?.tags || []));
    } catch (e) { console.error('Error fetching tag counts:', e); }
  };

  const fetchTriggerWords = async () => {
    try {
      const r = await axios.get(`${API}/admin/category-triggers`);
      setTriggerWords(r.data || []);
    } catch (e) { console.error('Error fetching trigger words:', e); }
  };

  const fetchOsmCategories = async () => {
    try {
      const r = await axios.get(`${API}/admin/osm-categories`);
      setOsmCategories(Array.isArray(r.data) ? r.data : []);
    } catch (e) { console.error('Error fetching OSM categories:', e); }
  };

  const fetchFeatureFlags = async () => {
    try {
      const r = await axios.get(`${API}/admin/feature-flags`);
      // Never round-trip the secret into the editable field. Treat a returned
      // `key_set` flag (or any non-empty stored value) as "a key is set".
      const keySet = r.data?.google_maps_api_key_set ?? r.data?.key_set
        ?? r.data?.google_maps_key_set ?? !!(r.data?.google_maps_api_key);
      setFeatureFlags({
        google_maps_autocomplete: r.data?.google_maps_autocomplete || false,
        google_maps_api_key: '',
        google_maps_key_set: !!keySet,
      });
    } catch (e) { console.error('Error fetching feature flags:', e); }
  };

  const fetchStateCoverage = async () => {
    setCoverageLoading(true);
    try {
      const r = await axios.get(`${API}/admin/state-coverage`);
      setStateCoverage(r.data || []);
    } catch (e) {
      console.error('Error fetching state coverage:', e);
      toast.error('Failed to load state coverage');
    }
    setCoverageLoading(false);
  };

  const fetchBackfillStatus = async () => {
    try {
      const r = await axios.get(`${API}/admin/state-backfill/status`);
      setBackfillStatus(r.data);
      setBackfillRunning(r.data?.running || false);
    } catch (e) { console.error('Error fetching backfill status:', e); }
  };

  const triggerBackfill = async (countryCodes = null) => {
    // Guard against starting a duplicate poll if one is already in flight.
    if (backfillPollRef.current) {
      toast('A backfill is already running');
      return;
    }
    setBackfillRunning(true);
    try {
      const body = countryCodes ? { country_codes: countryCodes } : {};
      const r = await axios.post(`${API}/admin/state-backfill`, body);
      toast.success(r.data?.message || 'Backfill started');
      // Bail out if the status endpoint keeps failing, so a persistently-down
      // poll can't leave the interval spinning and `backfillRunning` stuck.
      const MAX_POLL_ERRORS = 5;
      let pollErrors = 0;
      backfillPollRef.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/admin/state-backfill/status`);
          pollErrors = 0;
          setBackfillStatus(s.data);
          if (!s.data?.running) {
            clearInterval(backfillPollRef.current);
            backfillPollRef.current = null;
            setBackfillRunning(false);
            fetchStateCoverage();
            toast.success('Backfill complete');
          }
        } catch (err) {
          console.error('Error polling backfill status:', err);
          pollErrors += 1;
          if (pollErrors >= MAX_POLL_ERRORS) {
            clearInterval(backfillPollRef.current);
            backfillPollRef.current = null;
            setBackfillRunning(false);
            toast.error('Lost contact with backfill status — stopped polling.');
          }
        }
      }, 3000);
    } catch (e) {
      console.error('Error triggering backfill:', e);
      toast.error('Failed to start backfill');
      setBackfillRunning(false);
    }
  };

  // Clear any in-flight backfill poll when the component unmounts.
  useEffect(() => () => {
    if (backfillPollRef.current) {
      clearInterval(backfillPollRef.current);
      backfillPollRef.current = null;
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const r = await authGet('/auth/me');
        if (!r.data || r.data.role !== 'admin') { navigate('/admin/login'); return; }
        fetchSettings();
        fetchMapTags();
        fetchCategoryCounts();
        fetchTagCounts();
        fetchTriggerWords();
        fetchOsmCategories();
        fetchFeatureFlags();
      } catch (e) {
        navigate('/admin/login');
      }
    };
    checkAuth();
  }, [navigate]);

  // Lazy-load coverage tab
  useEffect(() => {
    if (activeTab === 'state_coverage' && stateCoverage.length === 0 && !coverageLoading) {
      fetchStateCoverage();
      fetchBackfillStatus();
    }
  }, [activeTab]); // eslint-disable-line

  // ─── Persistence ───────────────────────────────────────────────────────────
  // Persist the current (locally-batched) selection once. `null` = send an
  // empty list → backend treats as "all enabled". This POST busts an
  // expensive cache, so it only fires on an explicit Save (not per-toggle).
  const persistEnabledCountries = async () => {
    // The backend contract is empty list == "all enabled", so there is no way
    // to express "disable every country". An empty (but non-null) selection
    // would silently re-enable everything — block the save and tell the admin.
    if (enabledCountries !== null && enabledCountries.size === 0) {
      toast.error('Select at least one country, or use “Enable all” for worldwide.');
      return;
    }
    try {
      const payload = enabledCountries === null ? [] : Array.from(enabledCountries);
      await axios.post(`${API}/admin/general-settings`, {
        enabled_countries: payload,
      });
      setCountriesDirty(false);
      toast.success(enabledCountries === null ? 'All countries enabled' : 'Countries saved');
    } catch (e) {
      console.error('Error saving countries:', e);
      toast.error('Failed to save');
    }
  };

  // Toggle a single country in LOCAL state only — no network call until Save.
  const handleCountryToggle = (code, enabled) => {
    // When coming from the "all enabled" default, materialise the set
    // with every country then flip the toggled one.
    const base = enabledCountries === null
      ? new Set(ALL_COUNTRIES.map(c => c.code))
      : new Set(enabledCountries);
    if (enabled) base.add(code); else base.delete(code);
    // If the user has now ticked every country, collapse back to null
    // (empty list on the wire) so the "all" semantic is preserved.
    setEnabledCountries(base.size === ALL_COUNTRIES.length ? null : base);
    setCountriesDirty(true);
  };

  // "Enable all" — local only; collapses to the "all" (null) sentinel.
  const enableAllCountries = () => {
    setEnabledCountries(null);
    setCountriesDirty(true);
  };

  // ─── Map Tag handlers ──────────────────────────────────────────────────────
  const saveMapTags = async (tags) => {
    try {
      await axios.post(`${API}/admin/map-tags`, { tags });
      setMapTags(tags);
      fetchTagCounts();
      toast.success('Map tags saved');
    } catch (e) {
      console.error('Error saving map tags:', e);
      toast.error('Failed to save map tags');
    }
  };

  const handleToggleTagVisibility = (tag) => {
    saveMapTags(mapTags.map(t => t.name === tag.name ? { ...t, visible: t.visible === false } : t));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = mapTags.findIndex(t => t.name === active.id);
      const newIndex = mapTags.findIndex(t => t.name === over.id);
      const newTags = arrayMove(mapTags, oldIndex, newIndex).map((t, idx) => ({ ...t, order: idx }));
      saveMapTags(newTags);
    }
  };

  const handleOpenTagDialog = (tag = null) => {
    if (tag) {
      setEditingTag(tag);
      setTagForm({ name: tag.name, categories: tag.categories || [], visible: tag.visible !== false });
    } else {
      setEditingTag(null);
      setTagForm({ name: '', categories: [], visible: true });
    }
    setMapTagDialog(true);
  };

  const handleSaveTag = () => {
    const name = tagForm.name.trim();
    if (!name) { toast.error('Please enter a tag name'); return; }
    // Enforce unique tag names (case-insensitive), ignoring the row being edited.
    const dup = mapTags.some(t => t !== editingTag && t.name.toLowerCase() === name.toLowerCase());
    if (dup) { toast.error('A tag with that name already exists'); return; }
    // Match the edited row by identity so tags sharing a name aren't all mutated.
    const newTags = editingTag
      ? mapTags.map(t => t === editingTag ? { ...t, name, categories: tagForm.categories } : t)
      : [...mapTags, { name, categories: tagForm.categories, order: mapTags.length }];
    saveMapTags(newTags);
    setMapTagDialog(false);
  };

  const handleDeleteTag = (tagName) => {
    if (tagName === 'All') { toast.error('Cannot delete the "All" tag'); return; }
    saveMapTags(mapTags.filter(t => t.name !== tagName));
  };

  const toggleTagCategory = (category) => {
    setTagForm(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category],
    }));
  };

  // ─── Trigger words editor (category-level auto-categorization) ─────────────
  const openTriggerDialog = async (category) => {
    const existing = triggerWords.find(t => t.category === category);
    const triggers = existing ? (existing.triggers || []).join(', ') : '';
    let businesses = [];
    try {
      const r = await axios.get(`${API}/admin/businesses-by-category?category=${encodeURIComponent(category)}`);
      businesses = r.data || [];
    } catch (e) { console.error(e); }
    setTriggerEdit({ category, triggers, businesses });
    setTriggerDialog(true);
  };

  const saveTriggerEdit = async () => {
    const triggerArray = triggerEdit.triggers.split(',').map(t => t.trim()).filter(Boolean);
    try {
      if (triggerArray.length) {
        const r = await axios.post(`${API}/admin/category-triggers`, {
          category: triggerEdit.category,
          triggers: triggerArray,
        });
        const updated = r.data?.updated_count || 0;
        toast.success(updated > 0
          ? `Trigger words saved. ${updated} BTCMap businesses auto-categorized.`
          : 'Trigger words saved');
      } else {
        await axios.delete(`${API}/admin/category-triggers/${encodeURIComponent(triggerEdit.category)}`);
        toast.success('Trigger words cleared');
      }
      fetchTriggerWords();
      fetchCategoryCounts();
      fetchTagCounts();
    } catch (e) {
      console.error('Error saving triggers:', e);
      toast.error('Failed to save trigger words');
    }
    setTriggerDialog(false);
  };

  // ─── Save feature flags ────────────────────────────────────────────────────
  const saveFeatureFlags = async () => {
    setSavingFeatures(true);
    try {
      const payload = {
        google_maps_autocomplete: featureFlags.google_maps_autocomplete,
      };
      // Only transmit a new key when the admin actually typed one; otherwise
      // omit it so the server keeps the existing (masked) secret.
      const newKey = featureFlags.google_maps_api_key.trim();
      if (newKey) payload.google_maps_api_key = newKey;
      await axios.post(`${API}/admin/feature-flags`, payload);
      toast.success('Feature flags saved');
      if (newKey) {
        // Clear the typed value and reflect that a key is now set.
        setFeatureFlags(prev => ({ ...prev, google_maps_api_key: '', google_maps_key_set: true }));
      }
    } catch (e) {
      console.error('Error saving feature flags:', e);
      toast.error('Failed to save feature flags');
    }
    setSavingFeatures(false);
  };

  // ─── Render tabs ───────────────────────────────────────────────────────────
  const filteredCountries = countrySearch.trim()
    ? ALL_COUNTRIES.filter(c =>
        c.code.toLowerCase().includes(countrySearch.toLowerCase()) ||
        c.name.toLowerCase().includes(countrySearch.toLowerCase()))
    : ALL_COUNTRIES;

  const renderCountriesTab = () => {
    const allEnabled = enabledCountries === null;
    const enabledSize = allEnabled ? ALL_COUNTRIES.length : enabledCountries.size;
    const isChecked = (code) => allEnabled || enabledCountries.has(code);
    return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-sm text-muted-foreground">
          Toggle which countries surface in the directory. List is sourced from
          <code className="text-xs bg-muted px-1 rounded mx-1">frontend/src/data/countries.json</code>.
          Enabled: <strong>{enabledSize}</strong> / {ALL_COUNTRIES.length}
          {allEnabled && <span className="ml-1 text-xs italic">(no filter — default)</span>}.
          {!allEnabled && enabledSize === 0 && (
            <span className="ml-1 text-xs text-red-600 font-medium">
              “Disable all” isn’t supported — pick at least one country, or “Enable all” for worldwide.
            </span>
          )}
          {countriesDirty && <span className="ml-1 text-xs text-yellow-600 font-medium">Unsaved — click “Save countries”.</span>}
        </p>
        <div className="flex items-center gap-2">
          {countriesDirty && (
            <button
              type="button"
              onClick={persistEnabledCountries}
              className="text-xs px-2 py-1 bg-primary text-white rounded hover:bg-primary-hover"
            >
              Save countries
            </button>
          )}
          {!allEnabled && (
            <button
              type="button"
              onClick={enableAllCountries}
              className="text-xs px-2 py-1 border border-border rounded hover:bg-accent"
            >
              Enable all
            </button>
          )}
          <Input
            value={countrySearch}
            onChange={(e) => setCountrySearch(e.target.value)}
            placeholder="Search country…"
            className="max-w-xs"
          />
        </div>
      </div>
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted sticky top-0">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase w-20">Code</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Country</th>
              <th className="px-4 py-2 text-center text-xs font-medium text-muted-foreground uppercase w-20">Enabled</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredCountries.map(c => (
              <tr key={c.code} className="hover:bg-muted/50">
                <td className="px-4 py-2 font-mono text-xs">{c.code}</td>
                <td className="px-4 py-2 font-medium">{c.name}</td>
                <td className="px-4 py-2 text-center">
                  <Switch
                    checked={isChecked(c.code)}
                    onCheckedChange={(checked) => handleCountryToggle(c.code, checked)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    );
  };

  const renderCategoriesTab = () => {
    // Bucket every OSM amenity_type under the OL category whose triggers
    // match it first. Unmatched values land in the "Unmapped" bucket so
    // admins can decide where to route them.
    const osmByCategory = {};
    const unmapped = [];
    for (const row of osmCategories) {
      const val = (row.amenity_type || '').toLowerCase();
      if (!val) continue;
      let placed = null;
      for (const tw of triggerWords) {
        const words = (tw.triggers || []).map(t => String(t).toLowerCase());
        if (words.some(w => w && val.includes(w))) {
          placed = tw.category;
          break;
        }
      }
      if (placed) {
        (osmByCategory[placed] = osmByCategory[placed] || []).push(row);
      } else {
        unmapped.push(row);
      }
    }
    const filterRow = (rows) => osmFilter
      ? rows.filter(r => (r.amenity_type || '').toLowerCase().includes(osmFilter.toLowerCase()))
      : rows;

    return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground flex-1 min-w-0">
          Category taxonomy is sourced from
          <code className="text-xs bg-muted px-1 rounded mx-1">backend/services/categories.json</code>.
          Expand a row to see the OSM amenity/shop/tourism values that its
          trigger words match in the current dataset. Triggers auto-apply
          to OrangeLabel-native listings only — BTCMap/OSM imports keep
          their upstream taxonomy.
        </p>
        <input
          type="search"
          value={osmFilter}
          onChange={e => setOsmFilter(e.target.value)}
          placeholder="Filter OSM values…"
          className="px-2 py-1 text-xs rounded-md border border-border bg-background w-56 flex-shrink-0"
        />
      </div>
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase w-8"></th>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Category</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase w-24">OSM-native</th>
              <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground uppercase w-32">Businesses</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground uppercase w-28">OSM values</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground uppercase w-24">Triggers</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {CATEGORIES_DATA.filter(c => c.slug).map(c => {
              const counts = categoryCounts[c.name] || { orangelabel: 0, btcmap: 0 };
              const tw = triggerWords.find(t => t.category === c.name);
              const osm = filterRow(osmByCategory[c.name] || []);
              const allOsm = osmByCategory[c.name] || [];
              const isOpen = !!expandedCats[c.slug];
              const hasRows = osm.length > 0;
              return (
                <React.Fragment key={c.slug}>
                  <tr className="hover:bg-muted/50">
                    <td className="px-3 py-2 align-middle">
                      {hasRows && (
                        <button
                          type="button"
                          onClick={() => setExpandedCats(p => ({ ...p, [c.slug]: !p[c.slug] }))}
                          aria-label={isOpen ? 'Collapse' : 'Expand'}
                          className="text-muted-foreground hover:text-foreground text-xs w-4"
                        >
                          {isOpen ? '▾' : '▸'}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2 font-medium">{c.name}</td>
                    <td className="px-3 py-2">
                      {c.osm_native ? (
                        <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded">OSM</span>
                      ) : (
                        <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">OL-only</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex justify-center gap-2 text-xs">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded" title="OrangeLabel-native">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                          {counts.orangelabel || 0}
                        </span>
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded" title="BTCMap-imported">
                          <span className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
                          {counts.btcmap || 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-muted-foreground">
                      {allOsm.length || '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => openTriggerDialog(c.name)}
                        className="text-xs px-2 py-1 border border-border rounded hover:bg-accent transition-colors inline-flex items-center gap-1"
                        title="Edit trigger words"
                      >
                        <Pencil className="w-3 h-3" />
                        {tw?.triggers?.length ? `${tw.triggers.length}` : 'Set'}
                      </button>
                    </td>
                  </tr>
                  {isOpen && hasRows && (
                    <tr className="bg-muted/30">
                      <td></td>
                      <td colSpan={5} className="px-3 py-2">
                        <div className="flex flex-wrap gap-1.5">
                          {osm.map(r => (
                            <span
                              key={r.amenity_type}
                              className="inline-flex items-center gap-1.5 text-[10px] px-1.5 py-0.5 bg-card border border-border rounded"
                              title={`${r.count} total · BTCMap ${r.btcmap_count} · OL ${r.ol_count}`}
                            >
                              <span className="font-mono">{r.amenity_type}</span>
                              <span className="text-muted-foreground">×{r.count}</span>
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

            {/* Unmapped OSM values — not covered by any category's triggers */}
            {unmapped.length > 0 && (() => {
              const filteredUn = filterRow(unmapped);
              const isOpen = !!expandedCats['__unmapped'];
              return (
                <React.Fragment>
                  <tr className="hover:bg-muted/50 bg-amber-50 dark:bg-amber-900/10">
                    <td className="px-3 py-2">
                      {filteredUn.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setExpandedCats(p => ({ ...p, __unmapped: !p.__unmapped }))}
                          className="text-muted-foreground hover:text-foreground text-xs w-4"
                        >
                          {isOpen ? '▾' : '▸'}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2 font-medium italic text-amber-800 dark:text-amber-200">
                      Unmapped OSM values
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 px-1.5 py-0.5 rounded">needs trigger</span>
                    </td>
                    <td className="px-3 py-2 text-center text-xs text-muted-foreground">—</td>
                    <td className="px-3 py-2 text-right text-xs text-muted-foreground">{unmapped.length}</td>
                    <td className="px-3 py-2"></td>
                  </tr>
                  {isOpen && filteredUn.length > 0 && (
                    <tr className="bg-amber-50/50 dark:bg-amber-900/5">
                      <td></td>
                      <td colSpan={5} className="px-3 py-2">
                        <div className="flex flex-wrap gap-1.5">
                          {filteredUn.map(r => (
                            <span
                              key={r.amenity_type}
                              className="inline-flex items-center gap-1.5 text-[10px] px-1.5 py-0.5 bg-card border border-amber-200 dark:border-amber-800 rounded"
                              title={`${r.count} total · BTCMap ${r.btcmap_count} · OL ${r.ol_count}`}
                            >
                              <span className="font-mono">{r.amenity_type}</span>
                              <span className="text-muted-foreground">×{r.count}</span>
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })()}
          </tbody>
        </table>
      </div>
    </div>
    );
  };

  const renderTagsTab = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Quick filter tags shown on the map. Drag to reorder.</p>
        <button
          onClick={() => handleOpenTagDialog()}
          className="px-3 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors inline-flex items-center gap-1"
        >
          <Plus className="w-4 h-4" /> Add Tag
        </button>
      </div>
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase w-10" />
              <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Tag Name</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase w-28">Businesses</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Categories</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground uppercase w-32">Actions</th>
            </tr>
          </thead>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={mapTags.map(t => t.name)} strategy={verticalListSortingStrategy}>
              <tbody className="divide-y divide-border">
                {mapTags.map(tag => (
                  <SortableTagItem
                    key={tag.name}
                    tag={tag}
                    onEdit={handleOpenTagDialog}
                    onDelete={handleDeleteTag}
                    onToggleVisibility={handleToggleTagVisibility}
                    counts={tagCounts}
                  />
                ))}
              </tbody>
            </SortableContext>
          </DndContext>
        </table>
      </div>
    </div>
  );

  const renderFeaturesTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Feature Flags</h3>
        <p className="text-sm text-muted-foreground">Enable or disable optional features across the site</p>
      </div>
      <div className="border border-border rounded-lg p-4 space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              <h4 className="font-medium text-foreground">Google Maps Autocomplete</h4>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Enable business-name autocomplete on the submission page using the Google Maps Places API.
              When users type a name, matching suggestions surface and selecting one auto-fills the address fields.
            </p>
          </div>
          <Switch
            checked={featureFlags.google_maps_autocomplete}
            onCheckedChange={(checked) => setFeatureFlags({ ...featureFlags, google_maps_autocomplete: checked })}
          />
        </div>
        {featureFlags.google_maps_autocomplete && (
          <div className="pt-3 border-t border-border space-y-3">
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Key className="w-4 h-4" />Google Maps API Key</Label>
              {featureFlags.google_maps_key_set && (
                <p className="text-xs text-green-600">
                  A key is currently set. Leave this blank to keep it, or enter a new key to replace it.
                </p>
              )}
              <Input
                type="password"
                value={featureFlags.google_maps_api_key}
                onChange={(e) => setFeatureFlags({ ...featureFlags, google_maps_api_key: e.target.value })}
                placeholder={featureFlags.google_maps_key_set
                  ? 'Key set — enter a new key to replace it'
                  : 'Enter your Google Maps API key'}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Required APIs: Maps JavaScript API, Places API.
                <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">
                  Get API key
                </a>
              </p>
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end pt-4 border-t border-border">
        <button
          onClick={saveFeatureFlags}
          disabled={savingFeatures}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50 inline-flex items-center gap-2"
        >
          {savingFeatures ? (
            <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
          ) : 'Save Feature Flags'}
        </button>
      </div>
    </div>
  );

  const renderCoverageTab = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            State/province coverage for the top 10 countries by BTCMap listing count.
            Run backfill to populate missing <code className="text-xs bg-muted px-1 rounded">addr_state</code> via Nominatim.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchStateCoverage}
            disabled={coverageLoading}
            className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-muted transition-colors inline-flex items-center gap-1 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${coverageLoading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button
            onClick={() => triggerBackfill()}
            disabled={backfillRunning}
            className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors inline-flex items-center gap-1 disabled:opacity-50"
          >
            {backfillRunning ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Running...</>
            ) : (<><MapPin className="w-4 h-4" /> Backfill All</>)}
          </button>
        </div>
      </div>
      {backfillStatus && (backfillStatus.running || backfillStatus.completed_at) && (
        <div className={`border rounded-lg p-3 text-sm ${backfillStatus.running ? 'border-indigo-300 bg-indigo-50 dark:bg-indigo-950/30' : 'border-green-300 bg-green-50 dark:bg-green-950/30'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium">
              {backfillStatus.running
                ? `Backfill running — current: ${backfillStatus.current_country || '…'}`
                : `Backfill completed at ${backfillStatus.completed_at?.slice(0, 19).replace('T', ' ')} UTC`}
            </span>
            {backfillStatus.error && <span className="text-red-600 text-xs">{backfillStatus.error}</span>}
          </div>
          {backfillStatus.progress && (
            <div className="flex flex-wrap gap-2">
              {Object.entries(backfillStatus.progress).map(([cc, p]) => (
                <span key={cc} className="text-xs bg-white dark:bg-black/30 border border-border px-2 py-0.5 rounded font-mono">
                  {cc}: {p.done}/{p.total}
                  {p.errors > 0 && <span className="text-red-500 ml-1">({p.errors} err)</span>}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      {coverageLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading coverage data…</div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase w-12">#</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Country</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-muted-foreground uppercase w-24">Total</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-muted-foreground uppercase w-28">With State</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Coverage</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-muted-foreground uppercase w-20">States</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground uppercase w-24">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {stateCoverage.map((row, idx) => {
                const pct = row.total > 0 ? Math.round((row.with_state / row.total) * 100) : 0;
                const isExpanded = expandedCountry === row.country_code;
                return (
                  <React.Fragment key={row.country_code}>
                    <tr className="hover:bg-muted/50">
                      <td className="px-4 py-2 text-muted-foreground font-mono text-xs">{idx + 1}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{row.country_code}</span>
                          <span className="font-medium">{row.country_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-center font-mono text-xs">{row.total.toLocaleString()}</td>
                      <td className="px-4 py-2 text-center font-mono text-xs">{row.with_state.toLocaleString()}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-muted rounded-full h-1.5 max-w-[120px]">
                            <div
                              className={`h-1.5 rounded-full ${pct >= 75 ? 'bg-green-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-400'}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className={`text-xs font-medium ${pct >= 75 ? 'text-green-600' : pct >= 40 ? 'text-amber-600' : 'text-red-500'}`}>
                            {pct}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          onClick={() => setExpandedCountry(isExpanded ? null : row.country_code)}
                          className="text-xs text-primary hover:underline"
                        >
                          {row.states?.length || 0} {isExpanded ? '▲' : '▼'}
                        </button>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => triggerBackfill([row.country_code])}
                          disabled={backfillRunning}
                          className="text-xs px-2 py-1 border border-indigo-300 text-indigo-600 rounded hover:bg-indigo-50 dark:hover:bg-indigo-950/30 disabled:opacity-40 transition-colors"
                          title={`Backfill ${row.country_code}`}
                        >
                          Fill
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={7} className="px-4 py-3 bg-muted/30">
                          <div className="flex flex-wrap gap-1.5">
                            {(row.states || []).map(s => (
                              <span key={s.code} className="text-xs bg-card border border-border px-2 py-0.5 rounded font-mono">
                                {s.code} <span className="text-muted-foreground font-sans">{s.name}</span>
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        State data source: ISO 3166-2 subdivisions. Backfill uses Nominatim (1 req/s) — large countries may take several minutes.
      </p>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'countries':      return renderCountriesTab();
      case 'categories':     return renderCategoriesTab();
      case 'tags':           return renderTagsTab();
      case 'features':       return renderFeaturesTab();
      case 'state_coverage': return renderCoverageTab();
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-muted">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate('/admin')} className="p-2 hover:bg-muted rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground" data-testid="general-settings-heading">
              General Settings
            </h1>
            <p className="text-sm text-muted-foreground">Enable countries, edit category trigger words, configure map tags and feature flags.</p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading settings...</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="flex border-b border-border overflow-x-auto">
              {TABS.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                      activeTab === tab.id
                        ? 'border-primary text-primary bg-primary/5'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${activeTab === tab.id ? tab.color : ''}`} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
            <div className="p-6">{renderTabContent()}</div>
          </div>
        )}
      </div>

      {/* Trigger-words editor (categories are read-only; this edits only the triggers) */}
      <Dialog open={triggerDialog} onOpenChange={setTriggerDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Trigger words — {triggerEdit.category}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-orange-500" />
                Auto-categorization triggers
              </Label>
              <Input
                value={triggerEdit.triggers}
                onChange={(e) => setTriggerEdit({ ...triggerEdit, triggers: e.target.value })}
                placeholder="cafe, restaurant, pizza, food"
              />
              <p className="text-xs text-muted-foreground">
                Comma-separated. BTCMap businesses with matching amenity types are auto-categorized when saved.
              </p>
              {triggerEdit.triggers.trim() && (
                <div className="mt-2">
                  <p className="text-xs text-muted-foreground mb-1">Current triggers:</p>
                  <div className="flex flex-wrap gap-1">
                    {triggerEdit.triggers.split(',').map(t => t.trim()).filter(Boolean).map((t, i) => (
                      <span key={i} className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-1.5 py-0.5 rounded">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {(() => {
                const doc = triggerWords.find(t => t.category === triggerEdit.category);
                if (doc && doc.sample_amenity_types?.length) {
                  return (
                    <div className="mt-2 p-2 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-md">
                      <p className="text-xs font-medium text-cyan-700 dark:text-cyan-300 mb-1">
                        Matching BTCMap amenity types ({doc.matching_businesses} businesses):
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {doc.sample_amenity_types.map((at, i) => (
                          <span key={i} className="text-xs bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 px-1.5 py-0.5 rounded">
                            {at}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
            <div className="space-y-2 border-t border-border pt-4">
              <Label className="flex items-center justify-between">
                <span>Businesses in this category</span>
                <span className="text-sm text-muted-foreground">
                  {(() => {
                    const c = categoryCounts[triggerEdit.category];
                    if (!c) return '0 businesses';
                    if (typeof c === 'number') return `${c} businesses`;
                    return `${(c.orangelabel || 0) + (c.btcmap || 0)} businesses`;
                  })()}
                </span>
              </Label>
              {triggerEdit.businesses && triggerEdit.businesses.length > 0 ? (
                <div className="max-h-32 overflow-y-auto border border-border rounded-md divide-y divide-border">
                  {triggerEdit.businesses.slice(0, 20).map((b, i) => (
                    <div key={i} className="px-3 py-1.5 text-sm flex items-center justify-between">
                      <span className="truncate">{b.business_name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${b.btc_map ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300' : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'}`}>
                        {b.btc_map ? 'BTCMap' : 'OrangeLabel'}
                      </span>
                    </div>
                  ))}
                  {triggerEdit.businesses.length > 20 && (
                    <div className="px-3 py-1.5 text-xs text-muted-foreground text-center">
                      +{triggerEdit.businesses.length - 20} more
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No businesses in this category</p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setTriggerDialog(false)} className="px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors">
                Cancel
              </button>
              <button onClick={saveTriggerEdit} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover transition-colors">
                Save Triggers
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Map Tag Dialog */}
      <Dialog open={mapTagDialog} onOpenChange={setMapTagDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTag ? 'Edit Map Tag' : 'Add Map Tag'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Tag Name</Label>
              <Input
                value={tagForm.name}
                onChange={(e) => setTagForm({ ...tagForm, name: e.target.value })}
                placeholder="e.g., Food & Drink"
                className="bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label>Map to Categories</Label>
              <p className="text-xs text-muted-foreground mb-2">Select categories this tag filters. Leave empty to show all.</p>
              <div className="border border-border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                {ALL_CATEGORY_NAMES.map(cat => (
                  <div key={cat} className="flex items-center space-x-2">
                    <Checkbox
                      checked={tagForm.categories.includes(cat)}
                      onCheckedChange={() => toggleTagCategory(cat)}
                    />
                    <label className="text-sm cursor-pointer">{cat}</label>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setMapTagDialog(false)} className="px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors">
                Cancel
              </button>
              <button onClick={handleSaveTag} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover transition-colors">
                {editingTag ? 'Save Changes' : 'Add Tag'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GeneralSettings;
